import { compareShifts, compareTopLevel } from 'src/helper/quoteComparison';
import { groupQuoteRowsInEntryOrder } from '../quoteShiftOrder';
import { quoteWorksheetColumnPlan, quoteWorksheetWeights, QuoteWorksheetColumn } from '../quoteWorksheetColumns';
import { clientRequestWorksheetRows, isQuoteReference } from './clientRequestWorksheetRows';

/** Project saved values only. Never run today's agreement calculator on history. */
export function quoteHistoryPresentation(snapshot: any) {
  const header = snapshot.editorBuffers?.header ?? {};
  const value = { ...snapshot, ...header };
  const positions = snapshot.positions.map((row: any) => ({ ...row,
    rateTypeLabel: row.rateTypeLabel ?? row.quoteCalculation?.rateTypeLabel,
    ...(String(row.id) === String(snapshot.editorBuffers?.editingRowId) ? snapshot.editorBuffers.row : {}) }));
  const shiftsComparison = snapshot._base?.positions ? compareShifts(snapshot._base.positions, positions) : [];
  // Retain the reviewed proposal beside later Admin values in saved versions.
  for (const row of positions) {
    const decisions = (row.clientRequestDraftDecisions || []).filter(item => item.field !== '$row' &&
      ['accept', 'reject'].includes(item.decision));
    if (!decisions.length) continue;
    let comparison = shiftsComparison.find(item => String(item.originalData?.id) === String(row.id) ||
      String(item.historyData?.id) === String(row.id));
    if (!comparison) {
      comparison = { shiftNumber: row.shiftNumber, status: 'modified', changes: {}, originalData: { ...row }, historyData: { ...row } };
      shiftsComparison.push(comparison);
    }
    comparison.historyData = { ...comparison.historyData, id: decisions[0].historyShiftId };
    for (const decision of decisions) {
      comparison.changes[decision.field] = { base: decision.originalValue, requested: decision.requestedValue };
    }
  }
  const activeIds = snapshot.worksheetMembership?.activeRowIds;
  const projected = clientRequestWorksheetRows(positions, shiftsComparison, snapshot.worksheetMembership, [], true);
  const activePositions = projected.activeRows.filter((row: any) => !activeIds || activeIds.includes(String(row.id)));
  const worksheetPositions = projected.displayRows.map(row => !isQuoteReference(row) && activeIds && !activeIds.includes(String(row.id))
    ? { ...row, quoteReferenceLabel: 'Deleted' } : row);
  const totalFor = (row: any) => !isQuoteReference(row) && activePositions.includes(row) ? Number(row.totals ?? row.total ?? 0) : 0;
  const subtotal = activePositions.reduce((sum: number, row: any) => sum + totalFor(row), 0);
  const adminFee = value.adminFee ?? { type: 'fixed', amount: 0 };
  const adminFeeValue = adminFee.type === 'percentage' ? subtotal * Number(adminFee.amount) / 100 : Number(adminFee.amount);
  const names: QuoteWorksheetColumn[] = ['date', 'quantity', ...(value.showDressCode ? ['dress' as const] : []),
    ...(value.showArea ? ['area' as const] : []), 'position', ...(value.showToolsGear ? ['tools' as const] : []),
    'call-time', 'notes', 'rate-type', 'call-type', 'meal', 'rate', 'st', 'ot', 'dt',
    ...(value.includePerDiem ? ['per-diem' as const] : []), ...(value.includeMiscFees ? ['misc' as const] : []), 'total'];
  return { ...value, positions, worksheetPositions, activePositions,
    groupedData: groupQuoteRowsInEntryOrder(worksheetPositions, totalFor), adminFee, adminFeeValue,
    formattedGrandTotalWithAdminFee: subtotal + adminFeeValue - Number(value.discount?.amount ?? 0),
    hasPendingRequestRate: activePositions.some((row: any) => row.clientRatePending || (row.totals == null && row.total == null)),
    tableColumnCount: names.length, savedQuoteColumns: quoteWorksheetColumnPlan(names),
    savedQuoteCompactWidth: names.reduce((sum, name) => sum + quoteWorksheetWeights[name][1], 0),
    shiftsComparison,
    topLevelChanges: snapshot._base ? compareTopLevel(snapshot._base, value) : {},
    quoteData: {}, companyDetails: { name: value.payTo || 'Unavailable' },
  };
}
